import { apiErrorMessage } from '@agos/api-client'
import { useGetHealthMonitorChannels, usePostHealthMonitorTest } from '@agos/api-client/devices'
import { getGetSettingsSmsQueryKey, useGetSettingsSms, usePostSettingsSms } from '@agos/api-client/marketing'
import { T, tt, useAuth } from '@agos/shared'
import { ConfirmDialog, PageHeader, Spinner, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Info,
  Save,
  Send,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  ALERT_SOURCES,
  alertSourceLabel,
  alertSourceNote,
  alertingMessage,
  alertingState,
  channelMessage,
  emailProblem,
  formOf,
  mayTest,
  outcomeLabel,
  outcomeOf,
  payloadOf,
  phoneProblem,
  channelReadinessOf,
  readinessOf,
  recoveryCaveat,
  testNote,
  triggerNote,
  type MonitoringForm,
  type TestOutcome,
} from './monitoring-settings'

/**
 * `/settings-monitoring` — who is told when the system stops answering.
 *
 * **FB-153.** *Kritické alerty (SMS)* listed three sources — a 500, a failed health check, a failed backup —
 * and **one** exists. `HealthAlertService` has a single caller, the 60-second `healthMonitor()` job.
 * `GlobalExceptionHandler` has no alert path and `BackupService` writes its failure to `system_logs` and
 * stops. So a 500 storm and a failed nightly backup pass in silence on a page that promises an SMS for each.
 *
 * **FB-154.** The page stated a 30-minute cooldown per alert type, twice. There is none: the service is
 * **edge-triggered** — one message down, one back up, nothing between — and its docblock says why. The two
 * are different promises, and somebody who slept through the first SMS was told to expect a second.
 *
 * **And the two channels take their credentials from different places.** SMS uses the tenant's BulkGate pair
 * from `sms_settings`; e-mail passes `null` to `EmailGateway`, falling back to a server property and a
 * hard-coded from-address. So an e-mail alert is two settings away from working, not one, and this page cannot
 * see the second.
 *
 * Ported from legacy `pages/settings-monitoring.tsx` (285 lines).
 */

export function SettingsMonitoringPage() {
  const settings = useGetSettingsSms()

  if (settings.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <MonitoringEditor settings={{ ...settings.data }} />
}

const FIELD =
  'px-3 py-2 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 w-64 disabled:opacity-60'

function MonitoringEditor({ settings }: { settings: Record<string, unknown> | undefined }) {
  const { toast } = useToast()
  const { user, hasPermission } = useAuth()
  const queryClient = useQueryClient()

  // FB-155. Its own request rather than a field on the settings row: the mail credential lives in
  // `loyalty_settings` and the BulkGate pair in `sms_settings`, so the two halves are configured on two
  // different screens and neither table can answer for the other.
  const alertChannels = useGetHealthMonitorChannels()

  const [form, setForm] = useState<MonitoringForm>(() => formOf(settings))
  const [confirmTest, setConfirmTest] = useState(false)
  const [results, setResults] = useState<{ sms: TestOutcome; email: TestOutcome } | null>(null)

  /* §9.758, BACKEND.md §10A.413 - **two writes, two rights.** The alert settings live on `sms_settings` and save
     through `POST /settings/sms`, which has asked `settings_sms` since §9.699 (FB-1005); the test alert is
     `POST /health-monitor/test`, which asks `settings_monitoring` now. One gate on the page's own right would offer
     somebody who holds only that one a save the server refuses - over a form whose read it refused too, so blank. */
  const writable = mayWrite('sms', user?.systemRole, hasPermission)
  const readOnly = readOnlyReason('sms', user?.systemRole, hasPermission)
  const testable = mayWrite('monitoring', user?.systemRole, hasPermission)
  const testReadOnly = readOnlyReason('monitoring', user?.systemRole, hasPermission)

  // The BulkGate pair lives on the same row, which is how this page can tell a number apart from a
  // number that can actually be texted.
  // FB-155. Both halves come from the server now. E-mail used to be unanswerable from here — the alert
  // path used a server property rather than the credential `/settings-email` configures, and nothing
  // reported whether it was set, so the screen could only say it did not know.
  const channels = channelReadinessOf(alertChannels.data)

  const readiness = readinessOf(form, channels.smsConfigured, channels.mailConfigured)
  const state = alertingState(form, readiness)
  const phoneShape = phoneProblem(form.healthAlertPhone)
  const emailShape = emailProblem(form.healthAlertEmail)

  const save = usePostSettingsSms({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsSmsQueryKey() })
        toast({ title: tt('Nastavenia monitoringu uložené') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Uloženie zlyhalo'),
          description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
        }),
    },
  })

  const test = usePostHealthMonitorTest({
    mutation: {
      onSuccess: (answer) => {
        const payload = (answer as { results?: Record<string, unknown> } | undefined)?.results ?? {}
        setResults({ sms: outcomeOf(payload.sms), email: outcomeOf(payload.email) })
        toast({ title: tt('Testovací alert odoslaný') })
      },
      onError: (error: unknown) => {
        setResults(null)
        toast({
          variant: 'destructive',
          title: tt('Test neprešel'),
          description: apiErrorMessage(error, tt('Server odmietol alebo je odosielacia služba nedostupná.')),
        })
      },
    },
  })

  const set = <K extends keyof MonitoringForm>(key: K, value: MonitoringForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Monitoring systému')}
        description={tt('Kto sa dozvie, že systém prestal odpovedať')}
        icon={<Activity className="w-5 h-5" />}
        actions={
          <button
            type="button"
            data-save
            disabled={!writable || save.isPending || phoneShape !== null || emailShape !== null}
            onClick={() => save.mutate({ data: { ...payloadOf(form) } })}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť</T>
          </button>
        }
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      <div
        className={cn(
          'rounded-2xl border px-4 py-3 flex items-start gap-3',
          state === 'on'
            ? 'border-emerald-500/40 bg-emerald-500/5'
            : 'border-amber-500/40 bg-amber-500/5',
        )}
        data-alerting={state}
      >
        {state === 'on' ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 space-y-1">
          <p
            className={cn(
              'text-sm font-semibold',
              state === 'on' ? 'text-emerald-500' : 'text-amber-500',
            )}
          >
            {alertingMessage(state)}
          </p>
          {/* FB-154. One message each way, and the recovery one can be lost. */}
          <p className="text-xs text-muted-foreground" data-trigger-note>
            {triggerNote()}
          </p>
          <p className="text-xs text-muted-foreground" data-recovery-caveat>
            {recoveryCaveat()}
          </p>
        </div>
      </div>

      <Section title={tt('Kam sa alert pošle')} icon={<Activity className="w-4 h-4 text-primary" />}>
        <Toggle
          label={tt('Zapnuté upozornenia')}
          desc={tt('Bez toho sa neodošle nič, ani keď je číslo vyplnené.')}
          value={form.healthAlertsEnabled}
          disabled={!writable}
          onToggle={() => set('healthAlertsEnabled', !form.healthAlertsEnabled)}
          testId="healthAlertsEnabled"
        />
        <Row label={tt('Telefón pre alerty')} desc={channelMessage('sms', readiness.sms)}>
          <input
            type="tel"
            aria-label={tt('Telefón pre alerty')}
            className={cn(FIELD, phoneShape !== null && 'border-red-500')}
            disabled={!writable}
            placeholder="+421 9XX XXX XXX"
            value={form.healthAlertPhone}
            onChange={(event) => set('healthAlertPhone', event.target.value)}
          />
          <p className="text-[11px]" data-channel="sms" data-channel-state={readiness.sms}>
            {phoneShape !== null ? (
              <span className="text-red-500">
                <T>Toto nie je telefónne číslo.</T>
              </span>
            ) : (
              <span className="text-muted-foreground">{channelMessage('sms', readiness.sms)}</span>
            )}
          </p>
        </Row>
        <Row label={tt('E-mail pre alerty')} desc={channelMessage('email', readiness.email)}>
          <input
            type="email"
            aria-label={tt('E-mail pre alerty')}
            className={cn(FIELD, emailShape !== null && 'border-red-500')}
            disabled={!writable}
            placeholder="alerts@example.sk"
            value={form.healthAlertEmail}
            onChange={(event) => set('healthAlertEmail', event.target.value)}
          />
          <p className="text-[11px]" data-channel="email" data-channel-state={readiness.email}>
            {emailShape !== null ? (
              <span className="text-red-500">
                <T>Toto nie je e-mailová adresa.</T>
              </span>
            ) : (
              <span className="text-muted-foreground">{channelMessage('email', readiness.email)}</span>
            )}
          </p>
        </Row>
      </Section>

      {/* FB-153. Three were listed; one is wired. */}
      <Section title={tt('Čo alert vyvolá')} icon={<ShieldAlert className="w-4 h-4 text-primary" />}>
        {ALERT_SOURCES.map((source) => (
          <div
            key={source.key}
            className="px-5 py-4 flex items-start gap-3"
            data-source={source.key}
            data-source-wired={source.wired ? 'yes' : 'no'}
          >
            {source.wired ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            )}
            <div className="min-w-0">
              <p
                className={cn(
                  'font-semibold text-sm',
                  source.wired ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {alertSourceLabel(source.key)}{' '}
                {!source.wired && (
                  <span className="text-[11px] font-normal">
                    - <T>neposiela sa</T>
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{alertSourceNote(source)}</p>
            </div>
          </div>
        ))}
      </Section>

      <Section title={tt('Test upozornení')} icon={<Send className="w-4 h-4 text-primary" />}>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-muted-foreground" data-test-note>
            {testNote(form)}
          </p>
          <button
            type="button"
            data-send-test
            disabled={!testable || test.isPending || !mayTest(form)}
            onClick={() => setConfirmTest(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-muted text-sm font-medium disabled:opacity-50"
          >
            {test.isPending ? <Spinner /> : <Send className="w-4 h-4" />} <T>Odoslať testovací alert</T>
          </button>
          {testReadOnly !== null && (
            <p className="text-[11px] text-amber-500" data-test-read-only>
              {testReadOnly}
            </p>
          )}

          {results !== null && (
            <div className="space-y-1" data-test-results>
              {(['sms', 'email'] as const).map((channel) => (
                <p
                  key={channel}
                  className="text-sm flex items-center gap-2"
                  data-result={channel}
                  data-result-state={results[channel]}
                >
                  {results[channel] === 'ok' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : results[channel] === 'not_configured' ? (
                    <Info className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  {channel === 'sms' ? 'SMS' : tt('E-mail')}: {outcomeLabel(results[channel])}
                </p>
              ))}
            </div>
          )}
        </div>
      </Section>

      <ConfirmDialog
        open={confirmTest}
        onClose={() => setConfirmTest(false)}
        title={tt('Odoslať skutočný testovací alert?')}
        description={testNote(form)}
        confirmLabel={tt('Odoslať')}
        icon={<Send className="w-5 h-5" />}
        loading={test.isPending}
        onConfirm={() => {
          setConfirmTest(false)
          test.mutate()
        }}
      />
    </div>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        {icon}
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</h2>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  )
}

function Row({
  label,
  desc,
  children,
}: {
  label: string
  desc: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4">
      <div className="flex-1 pt-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <div className="shrink-0 space-y-1">{children}</div>
    </div>
  )
}

function Toggle({
  label,
  desc,
  value,
  disabled,
  onToggle,
  testId,
}: {
  label: string
  desc: string
  value: boolean
  disabled: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4" data-toggle={testId}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-toggle-state={value ? 'on' : 'off'}
        onClick={onToggle}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
